import { useEffect, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { WorkerCombobox } from "@/components/WorkerCombobox";

const deriveInitials = (name) => (name || "").split(/[^A-Za-z0-9]+/).filter(Boolean).slice(0, 4).map((w) => w[0]).join("").toUpperCase() || "CL";

// Convert an accepted proposal → Client + Project, optionally assigning workers in the same step.
// With `alreadyConverted` (auto-converted on acceptance) it skips the convert call and only assigns.
export default function ConvertProposalModal({ proposal: p, isNewClient, alreadyConverted = false, onClose, onConverted }) {
  const [form, setForm] = useState({
    project_name: `${p.company_name} — ${p.location || "Project"}`, location: p.location || "",
    po_number: "", client_initials: deriveInitials(p.company_name),
  });
  const newRow = () => ({
    key: Math.random().toString(36).slice(2), worker_id: "", pay_rate: "",
    bill_rate: p.hourly_rate || 0, per_diem_rate: p.per_diem_applies ? (p.per_diem_rate || 0) : 0,
    start_date: p.anticipated_start_date || "", worker_email: "",
  });
  const [rows, setRows] = useState([newRow()]);
  const [assignLater, setAssignLater] = useState(false);
  const [sendNotice, setSendNotice] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/workers").then((r) => setWorkers(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  const wk = (id) => workers.find((w) => w.id === id);
  const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const needsEmail = (r) => sendNotice && r.worker_id && wk(r.worker_id) && !wk(r.worker_id).email;
  const inp = "mt-1 w-full h-11 px-3 border border-slate-300 rounded-md";

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const active = assignLater ? [] : rows.filter((r) => r.worker_id || r.pay_rate);
    if (!assignLater && active.length === 0) { setErr(alreadyConverted ? "Pick at least one worker to assign." : "Pick a worker to assign, or tick “Assign workers later”."); return; }
    for (const r of active) {
      if (!r.worker_id) { setErr("Every assignment row needs a worker (or remove the row)."); return; }
      if (!(parseFloat(r.pay_rate) > 0)) { setErr(`Enter a pay rate for ${wk(r.worker_id)?.name || "the worker"}.`); return; }
      if (needsEmail(r) && !r.worker_email.trim()) { setErr(`${wk(r.worker_id).name} has no email — add one below to send the notice, or untick “Send assignment notice”.`); return; }
    }
    const dupes = active.map((r) => r.worker_id).filter((id, i, a) => a.indexOf(id) !== i);
    if (dupes.length) { setErr("The same worker is listed twice."); return; }
    setBusy(true);
    try {
      let conv;
      if (alreadyConverted) {
        conv = { project_id: p.project_id_created, project_name: p.project_name_created || p.company_name, client_name: p.company_name };
      } else {
        const body = { project_name: form.project_name.trim(), po_number: form.po_number.trim(), location: form.location.trim() };
        if (isNewClient) body.client_initials = form.client_initials.trim();
        conv = (await api.post(`/proposals/${p.id}/convert-to-client-project`, body)).data;
        toast.success(conv.client_created
          ? `Converted — new client "${conv.client_name}" and project "${conv.project_name}" created.`
          : `Converted — project "${conv.project_name}" added under "${conv.client_name}".`);
      }
      const failed = [];
      let notices = 0;
      for (const r of active) {
        try {
          const { data } = await api.post("/assignments", {
            worker_id: r.worker_id, project_id: conv.project_id, status: "active",
            pay_rate: parseFloat(r.pay_rate) || 0, bill_rate: parseFloat(r.bill_rate) || 0,
            per_diem_rate: parseFloat(r.per_diem_rate) || 0, start_date: r.start_date || null,
            send_notice: sendNotice, worker_email: needsEmail(r) ? r.worker_email.trim() : undefined,
          });
          if (data.notice?.ok) notices += 1;
          else if (data.notice && !data.notice.ok) failed.push(`${wk(r.worker_id)?.name}: notice not sent (${data.notice.error})`);
        } catch (e2) {
          failed.push(`${wk(r.worker_id)?.name || "worker"}: ${e2.response?.data?.detail?.toString() || e2.message}`);
        }
      }
      if (active.length) {
        const ok = active.length - failed.filter((f) => !f.includes("notice not sent")).length;
        toast.success(`${ok} worker${ok === 1 ? "" : "s"} assigned to "${conv.project_name}"${sendNotice ? ` · ${notices} notice${notices === 1 ? "" : "s"} sent` : ""}.`);
      }
      if (failed.length) toast.error(`Check Assignments — ${failed.join("; ")}`, { duration: 12000 });
      onConverted(conv);
    } catch (e2) { setErr(e2.response?.data?.detail?.toString() || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 overflow-y-auto" data-testid="convert-proposal-modal" onClick={() => !busy && onClose()}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-auto my-8 p-6">
        <h2 className="text-xl font-bold">{alreadyConverted ? "Assign workers to this project" : "Convert to Client + Project"}</h2>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          {alreadyConverted
            ? <>Project <b>{p.project_name_created || p.company_name}</b> was created automatically when the client accepted. Pick who's working it.</>
            : isNewClient
            ? <>This creates a <b>new client</b> "{p.company_name}" and a project under it.</>
            : <>Client "{p.company_name}" already exists — this adds a <b>new project</b> under it.</>}
        </p>
        {!alreadyConverted && <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project name *</label>
            <input required value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} data-testid="convert-project-name" className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Location</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="convert-location" className={inp} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">PO number (optional)</label>
              <input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} data-testid="convert-po-number" placeholder="Add later if unknown" className={inp} />
            </div>
          </div>
          {isNewClient && (
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Client initials *</label>
              <input required value={form.client_initials} maxLength={6} onChange={(e) => setForm({ ...form, client_initials: e.target.value.toUpperCase() })}
                data-testid="convert-client-initials" className="mt-1 w-32 h-11 px-3 border border-slate-300 rounded-md font-mono uppercase" />
              <div className="text-xs text-slate-400 mt-1">Used to build invoice numbers (e.g. {form.client_initials || "ABC"}061526-1). Set once — don't change later.</div>
            </div>
          )}
        </div>}

        <div className={alreadyConverted ? "" : "mt-5 pt-4 border-t border-slate-200"}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="tinylabel">Assign workers to this project</div>
              <p className="text-xs text-slate-500 mt-0.5">Bill rate{p.per_diem_applies ? " and per diem" : ""} come from the proposal; enter each worker's pay rate.</p>
            </div>
            {!alreadyConverted && <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <input type="checkbox" checked={assignLater} onChange={(e) => { setAssignLater(e.target.checked); setErr(""); }} data-testid="convert-assign-later" className="h-4 w-4 accent-emerald-600" />
              Assign workers later
            </label>}
          </div>
          {!assignLater && (
            <div className="mt-3 space-y-3" data-testid="convert-assign-section">
              {rows.map((r, i) => (
                <div key={r.key} className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid={`convert-assign-row-${i}`}>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <div className="col-span-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Worker</label>
                      <WorkerCombobox value={r.worker_id} onChange={(v) => setRow(r.key, { worker_id: v })} workers={workers} testId={`convert-worker-${i}`} />
                    </div>
                    <div><label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Pay rate *</label>
                      <input type="number" step="0.01" min="0" value={r.pay_rate} onChange={(e) => setRow(r.key, { pay_rate: e.target.value })} data-testid={`convert-pay-${i}`} className={inp + " bg-white"} /></div>
                    <div><label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Bill rate</label>
                      <input type="number" step="0.01" min="0" value={r.bill_rate} onChange={(e) => setRow(r.key, { bill_rate: e.target.value })} data-testid={`convert-bill-${i}`} className={inp + " bg-white"} /></div>
                    <div><label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Per diem</label>
                      <input type="number" step="0.01" min="0" value={r.per_diem_rate} onChange={(e) => setRow(r.key, { per_diem_rate: e.target.value })} data-testid={`convert-perdiem-${i}`} className={inp + " bg-white"} /></div>
                  </div>
                  <div className="mt-2 flex items-end gap-2">
                    <div className="flex-1"><label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Start date</label>
                      <input type="date" value={r.start_date || ""} onChange={(e) => setRow(r.key, { start_date: e.target.value })} data-testid={`convert-start-${i}`} className={inp + " bg-white"} /></div>
                    {rows.length > 1 && (
                      <button type="button" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} data-testid={`convert-remove-row-${i}`}
                        title="Remove this worker" className="h-11 px-3 text-slate-400 hover:text-rose-600"><Trash size={16} weight="bold" /></button>
                    )}
                  </div>
                  {needsEmail(r) && (
                    <div className="mt-2 bg-amber-50 border border-amber-300 rounded-md p-2.5" data-testid={`convert-no-email-${i}`}>
                      <div className="text-xs font-semibold text-amber-900">{wk(r.worker_id).name} has no email on file — add it to send the assignment notice (saved to their record):</div>
                      <input type="email" value={r.worker_email} onChange={(e) => setRow(r.key, { worker_email: e.target.value })} placeholder="worker@example.com"
                        data-testid={`convert-worker-email-${i}`} className="mt-1.5 w-full h-10 px-3 border border-amber-300 rounded-md bg-white text-sm" />
                    </div>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button type="button" onClick={() => setRows((rs) => [...rs, newRow()])} data-testid="convert-add-worker"
                  className="text-emerald-700 hover:text-emerald-900 font-semibold text-sm inline-flex items-center gap-1"><Plus size={14} weight="bold" /> Add another worker</button>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={sendNotice} onChange={(e) => { setSendNotice(e.target.checked); setErr(""); }} data-testid="convert-send-notice" className="h-4 w-4 accent-emerald-600" />
                  Send assignment notice to worker{rows.length > 1 ? "s" : ""} (email + text, set-password link)
                </label>
              </div>
            </div>
          )}
        </div>

        {!alreadyConverted && <div className="mt-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
          The billing rate{p.ot_rate_applies ? ", overtime terms" : ""}{p.holiday_rate_applies ? ", holiday rate" : ""} and per-diem terms from the proposal carry over to the project automatically. Supervisors and holiday dates are set afterward on the Project.
        </div>}
        {err && <div className="text-rose-600 text-sm mt-3 font-semibold" data-testid="convert-error">{err}</div>}
        <div className="mt-5 flex gap-3 justify-end">
          <button type="button" disabled={busy} onClick={onClose} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
          <button data-testid="convert-submit-btn" disabled={busy} className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md disabled:opacity-50">
            {busy ? (alreadyConverted ? "Assigning…" : "Converting…") : (alreadyConverted ? "Assign workers" : "Convert")}
          </button>
        </div>
      </form>
    </div>
  );
}
